import React, { useState, useEffect, useRef, useMemo } from "react";
import { FiCalendar } from "react-icons/fi";
import {
  WorkoutPlan,
  Prescription,
  ExerciseInPlan,
  Exercise,
} from "../../../types";
import exercicies from "../../../services/exercicies";
import workoutplans from "../../../services/workoutplans";
import assistant, { ProgressData } from "../../../services/assistant";
import { toast } from "react-toastify";
import { WorkoutPlanAIModal } from "./WorkoutPlanAIModal";
import { useAnimatedPlaceholderWorkout } from "./useAnimatedPlaceholderWorkout";
import {
  WorkoutPlanHeader,
  WorkoutPlanFooter,
  AISection,
  PrescriptionSection,
} from "./WorkoutPlanForm";

interface WorkoutPlanFormProps {
  initialData?: WorkoutPlan | null;
  studentId: string;
  professionalId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const StudentWorkoutPlanForm = ({
  initialData,
  studentId,
  professionalId,
  onClose,
  onSuccess,
}: WorkoutPlanFormProps) => {
  const [name, setName] = useState(initialData?.name || "");

  const formatDateForInput = (dateString?: string): string => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return "";
      return date.toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  const [validade, setValidade] = useState(
    formatDateForInput(initialData?.end_date),
  );
  const [prescriptions, setPrescriptions] = useState<Prescription[]>(
    initialData?.prescriptions ?? initialData?.exercises ?? [],
  );
  const prescriptionsEndRef = useRef<HTMLDivElement>(null);
  const fetchedExerciseIdsRef = useRef<Set<number>>(new Set());

  const exerciseIdsToFetch = useMemo(() => {
    const ids: number[] = [];
    prescriptions.forEach((presc) => {
      presc.exercises.forEach((ex) => {
        if (
          ex.id &&
          ex.id > 0 &&
          !ex.image_url &&
          !ex.image &&
          !fetchedExerciseIdsRef.current.has(ex.id) &&
          !ids.includes(ex.id)
        ) {
          ids.push(ex.id);
        }
      });
    });
    return ids;
  }, [prescriptions]);

  useEffect(() => {
    if (exerciseIdsToFetch.length === 0) return;

    const fetchMissingExerciseImages = async () => {
      exerciseIdsToFetch.forEach((id) => {
        fetchedExerciseIdsRef.current.add(id);
      });

      try {
        const allExercises = await exercicies.list_all(
          professionalId,
          1,
          1000,
          "",
        );
        const exerciseMap = new Map<number, Exercise>();
        allExercises.forEach((ex) => {
          if (ex.id) {
            exerciseMap.set(ex.id, ex);
          }
        });

        setPrescriptions((prevPrescriptions) => {
          let hasChanges = false;
          const updated = prevPrescriptions.map((presc) => ({
            ...presc,
            exercises: presc.exercises.map((ex) => {
              if (
                ex.id &&
                exerciseIdsToFetch.includes(ex.id) &&
                !ex.image_url &&
                !ex.image
              ) {
                const fullExercise = exerciseMap.get(ex.id);
                if (
                  fullExercise &&
                  (fullExercise.image_url || fullExercise.image)
                ) {
                  hasChanges = true;
                  return {
                    ...ex,
                    image: fullExercise.image || fullExercise.image_url || "",
                    image_url:
                      fullExercise.image_url || fullExercise.image || "",
                  };
                }
              }
              return ex;
            }),
          }));
          return hasChanges ? updated : prevPrescriptions;
        });
      } catch (error) {
        console.error("Erro ao buscar dados completos dos exercícios:", error);
        exerciseIdsToFetch.forEach((id) => {
          fetchedExerciseIdsRef.current.delete(id);
        });
      }
    };

    fetchMissingExerciseImages();
  }, [exerciseIdsToFetch, professionalId]);

  const [loading, setLoading] = useState(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [aiDescription, setAIDescription] = useState("");
  const [aiLoading, setAILoading] = useState(false);
  const [aiResult, setAIResult] = useState<Prescription[] | null>(null);
  const [aiInputValue, setAIInputValue] = useState("");
  const [assistantId, setAssistantId] = useState<number | null>(null);
  const [aiProgress, setAIProgress] = useState<ProgressData | undefined>(
    undefined,
  );
  const isAnalyzingRef = React.useRef(false);

  const resetAnalyzingState = () => {
    setAILoading(false);
    setAIProgress(undefined);
    isAnalyzingRef.current = false;
  };

  const processAIResult = async (result: {
    success: boolean;
    response: string;
    error?: string;
  }) => {
    if (!result.success) {
      toast.error(result.error || "Erro ao processar descrição.");
      resetAnalyzingState();
      return;
    }

    let workoutData: { prescriptions: Prescription[] } | null = null;
    const jsonRegex = /\{[\s\S]*\}/;
    const jsonMatch = jsonRegex.exec(result.response);
    if (jsonMatch) {
      try {
        workoutData = JSON.parse(jsonMatch[0]);
      } catch (e) {
        console.error("Erro ao parsear JSON:", e);
      }
    }

    if (!workoutData?.prescriptions) {
      toast.warning(
        "A IA não retornou um formato estruturado. Tente ser mais específico na descrição.",
      );
      resetAnalyzingState();
      return;
    }

    interface AssistantExercise {
      id?: number;
      name?: string;
      sets?: number | string;
      reps?: number | string;
      repetitions?: number | string;
      series?: number | string;
      load?: string;
      interval?: number | string;
    }

    interface AssistantPrescription {
      name?: string;
      exercises?: AssistantExercise[];
    }

    const exercises = await getExercisesList();

    const mapExercise = (
      ex: AssistantExercise,
      availableExercises: Exercise[],
    ): ExerciseInPlan => {
      const exercise = availableExercises.find(
        (e) =>
          e.id === ex.id || e.name.toLowerCase() === ex.name?.toLowerCase(),
      );

      return {
        id: exercise?.id || ex.id || 0,
        name: exercise?.name || ex.name || "Exercício",
        sets: Number(ex.sets) || 3,
        reps: Number(ex.reps || ex.repetitions) || 10,
        series: Number(ex.sets || ex.series) || 3,
        repetitions: Number(ex.reps || ex.repetitions) || 10,
        load: String(ex.load || ""),
        interval: Number(ex.interval) || 60,
        professional: Number(professionalId),
        image: exercise?.image || exercise?.image_url || "",
        image_url: exercise?.image_url || exercise?.image || "",
      } as ExerciseInPlan;
    };

    const validPrescriptions: Prescription[] = (
      workoutData.prescriptions as AssistantPrescription[]
    ).map((presc, index) => ({
      name: presc.name || `Treino ${String.fromCodePoint(65 + index)}`,
      exercises: (presc.exercises || []).map((ex: AssistantExercise) =>
        mapExercise(ex, exercises),
      ),
    }));

    setAIResult(validPrescriptions);
    setAIDescription(aiInputValue);
    setIsAIModalOpen(true);
  };

  const isFormOpen = true;
  const animatedPlaceholder = useAnimatedPlaceholderWorkout({
    isOpen: isFormOpen,
    isAIModalOpen,
    aiInputValue,
  });

  const addPrescription = () => {
    setPrescriptions([
      ...prescriptions,
      {
        name: `Treino ${String.fromCodePoint(65 + prescriptions.length)}`,
        exercises: [],
      },
    ]);
    setTimeout(() => {
      prescriptionsEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }, 100);
  };

  const removePrescription = (index: number) => {
    const newPrescriptions = [...prescriptions];
    newPrescriptions.splice(index, 1);
    setPrescriptions(newPrescriptions);
  };

  const updatePrescriptionName = (index: number, newName: string) => {
    const newPrescriptions = [...prescriptions];
    newPrescriptions[index].name = newName;
    setPrescriptions(newPrescriptions);
  };

  const addExercise = (prescIndex: number) => {
    const newPrescriptions = [...prescriptions];
    newPrescriptions[prescIndex].exercises.push({
      id: 0,
      name: "",
      sets: "3",
      reps: "10",
      series: "3",
      repetitions: "10",
      load: "",
      interval: "60",
      professional: Number(professionalId),
    } as ExerciseInPlan);
    setPrescriptions(newPrescriptions);
  };

  const removeExercise = (prescIndex: number, exerIndex: number) => {
    const newPrescriptions = [...prescriptions];
    newPrescriptions[prescIndex].exercises.splice(exerIndex, 1);
    setPrescriptions(newPrescriptions);
  };

  const updateExercise = (
    prescIndex: number,
    exerIndex: number,
    field: keyof ExerciseInPlan,
    value: string | number,
  ) => {
    const newPrescriptions = [...prescriptions];
    const currentExercise = newPrescriptions[prescIndex].exercises[exerIndex];
    const valueAsText = typeof value === "number" ? String(value) : value;
    newPrescriptions[prescIndex].exercises[exerIndex] = {
      ...currentExercise,
      [field]: valueAsText,
    };

    if (field === "sets") {
      newPrescriptions[prescIndex].exercises[exerIndex].series = valueAsText;
    }
    if (field === "reps") {
      newPrescriptions[prescIndex].exercises[exerIndex].repetitions = valueAsText;
    }

    setPrescriptions(newPrescriptions);
  };

  useEffect(() => {
    const loadAssistant = async () => {
      try {
        const assistants = await assistant.listAssistants();
        const defaultAssistant =
          assistants.find(
            (a) => a.name === "Assistente Padrão" || a.exercicies_use,
          ) || assistants[0];
        if (defaultAssistant) {
          setAssistantId(defaultAssistant.id);
        }
      } catch (error) {
        console.error("Erro ao carregar assistentes:", error);
      }
    };
    loadAssistant();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      plan_name: name,
      professional_id: professionalId,
      user_id: studentId,
      validade: validade || null,
      prescriptions: prescriptions.map((p) => ({
        name: p.name,
        exercises: p.exercises.map((e) => {
          // Salvar séries, reps e carga como texto em todos os campos
          const sets = String(e.sets ?? e.series ?? "").trim();
          const reps = String(e.reps ?? e.repetitions ?? "").trim();
          const load = String(e.load ?? e.charge ?? "").trim();
          const interval = String(e.interval ?? "").trim();

          return {
            id: Number(e.id) || 0,
            name: e.name || "",
            sets,
            reps,
            series: sets,
            repetitions: reps,
            load,
            interval: interval || undefined,
            professional: Number(professionalId),
          };
        }),
      })),
    };

    let success = false;
    if (initialData?.id) {
      success = await workoutplans.update_plan(
        String(initialData.id),
        payload as unknown as WorkoutPlan,
      );
    } else {
      success = await workoutplans.create_plan(
        payload as unknown as WorkoutPlan,
      );
    }

    setLoading(false);
    if (success) {
      toast.success(
        `Plano de treino ${initialData ? "atualizado" : "criado"} com sucesso!`,
      );
      onSuccess();
    } else {
      toast.error("Erro ao salvar plano de treino.");
    }
  };

  const getExercisesList = async (): Promise<Exercise[]> => {
    try {
      const exercises = await exercicies.list_all(professionalId, 1, 100, "");
      return exercises;
    } catch (error) {
      console.error("Erro ao buscar exercícios:", error);
      return [];
    }
  };

  const handleAIAnalyze = async (text?: string) => {
    if (isAnalyzingRef.current) {
      return;
    }

    const description = text || aiInputValue.trim();
    if (!description) {
      toast.error("Por favor, insira uma descrição do treino.");
      return;
    }

    if (!assistantId) {
      toast.error(
        "Assistente não disponível. Tente novamente em alguns instantes.",
      );
      return;
    }

    isAnalyzingRef.current = true;
    setAIDescription(description);
    setIsAIModalOpen(true);
    setAILoading(true);
    setAIProgress(undefined);

    try {
      const result = await assistant.chatStream(
        {
          assistant_id: assistantId,
          message: description,
          user_id: studentId,
        },
        (progress) => {
          setAIProgress(progress);
        },
      );
      await processAIResult(result);
    } catch (error) {
      console.error("Erro ao processar descrição:", error);
      toast.error("Erro ao processar descrição.");
    } finally {
      resetAnalyzingState();
    }
  };

  const handleApplyAIResult = (newPrescriptions: Prescription[]) => {
    if (newPrescriptions.length === 0) {
      toast.warning("Nenhum treino selecionado para aplicar.");
      return;
    }
    setPrescriptions(newPrescriptions);
    toast.success("Treinos criados com sucesso!");
    setIsAIModalOpen(false);
    setAIResult(null);
    setAIDescription("");
    setAIInputValue("");
  };

  const handleCancelAI = () => {
    setIsAIModalOpen(false);
    setAIResult(null);
    setAIDescription("");
    setAIInputValue("");
    resetAnalyzingState();
  };

  const handleAudioAnalyze = async (audioBlob: Blob) => {
    if (!assistantId) {
      toast.error("Assistente não selecionado.");
      return;
    }

    setAILoading(true);
    isAnalyzingRef.current = true;
    setAIProgress(undefined);

    try {
      const result = await assistant.chatStream(
        {
          assistant_id: assistantId,
          message: aiInputValue || "",
          audio: audioBlob,
          user_id: studentId,
        },
        (progress) => {
          setAIProgress(progress);
        },
      );

      if (result.error) {
        toast.error(result.error);
        resetAnalyzingState();
        return;
      }

      await processAIResult(result);
    } catch (error) {
      console.error("Erro ao processar áudio:", error);
      toast.error("Erro ao processar áudio.");
      resetAnalyzingState();
    }
  };

  const handleImageAnalyze = async (imageFile: File) => {
    if (!assistantId) {
      toast.error("Assistente não selecionado.");
      return;
    }

    setAILoading(true);
    isAnalyzingRef.current = true;
    setAIProgress(undefined);

    try {
      const result = await assistant.chatStream(
        {
          assistant_id: assistantId,
          message: aiInputValue || "",
          image: imageFile,
          user_id: studentId,
        },
        (progress) => {
          setAIProgress(progress);
        },
      );

      if (result.error) {
        toast.error(result.error);
        resetAnalyzingState();
        return;
      }

      await processAIResult(result);
    } catch (error) {
      console.error("Erro ao processar imagem:", error);
      toast.error("Erro ao processar imagem.");
      resetAnalyzingState();
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col overflow-hidden overflow-x-hidden max-h-screen h-full w-screen transition-all duration-300"
    >
      <WorkoutPlanHeader initialData={initialData} onClose={onClose} />

      {/* Conteúdo Scrollável - Mobile First */}
      <div className="flex-1 space-y-3 overflow-y-auto overflow-x-hidden bg-gray-50/50 p-3 dark:bg-gray-900/20 min-w-0 sm:space-y-4 sm:p-4 md:space-y-8 md:p-8">
        {!initialData && (
          <AISection
            aiInputValue={aiInputValue}
            onInputChange={setAIInputValue}
            onAnalyze={handleAIAnalyze}
            onAudioAnalyze={handleAudioAnalyze}
            onImageAnalyze={handleImageAnalyze}
            placeholder={
              animatedPlaceholder ||
              "Criar treino de hipertrofia, 3x por semana..."
            }
            disabled={loading || aiLoading}
            loading={aiLoading}
          />
        )}

        {/* Nome do Plano e Validade - Mobile First */}
        <div className="grid grid-cols-1 gap-2 min-w-0 sm:gap-3 md:gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-stroke bg-white p-3 shadow-sm dark:border-strokedark dark:bg-boxdark min-w-0 overflow-hidden sm:rounded-xl md:rounded-2xl sm:p-4 md:p-6 md:pb-3 md:pt-4">
            <label
              htmlFor="plan-name"
              className="mb-2 block text-xs font-bold text-gray-700 dark:text-gray-300 sm:mb-2.5 sm:text-sm md:mb-3"
            >
              Nome do Plano <span className="text-red-500">*</span>
            </label>
            <input
              id="plan-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Hipertrofia 2024..."
              required
              className="w-full min-h-[48px] box-border rounded-xl border border-stroke bg-gray-50 px-3 py-2.5 text-sm outline-none transition focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary touch-manipulation max-w-full sm:min-h-0 sm:rounded-lg sm:px-3.5 sm:py-2.5 md:rounded-xl md:px-5 md:py-3.5 md:text-base"
            />
          </div>

          <div className="rounded-xl border border-stroke bg-white p-3 shadow-sm dark:border-strokedark dark:bg-boxdark overflow-hidden sm:rounded-xl md:rounded-2xl sm:p-4 md:p-6 md:pb-3 md:pt-4">
            <label className="mb-2 flex items-center gap-2 text-xs font-bold text-gray-700 dark:text-gray-300 sm:mb-2.5 sm:text-sm md:mb-3">
              <FiCalendar className="text-purple-600" size={14} />
              Validade do Treino
            </label>
            <input
              type="date"
              value={validade}
              onChange={(e) => setValidade(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              className="w-full min-h-[48px] box-border rounded-xl border border-stroke bg-gray-50 px-3 py-2.5 text-sm outline-none transition focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary touch-manipulation max-w-full sm:min-h-0 sm:rounded-lg sm:px-3.5 sm:py-2.5 md:rounded-xl md:px-5 md:py-3.5 md:text-base"
            />
            <p className="mt-1.5 text-[10px] text-gray-500 dark:text-gray-400 sm:mt-2 sm:text-xs">
              Data em que o plano expira
            </p>
          </div>
        </div>

        <PrescriptionSection
          prescriptions={prescriptions}
          professionalId={professionalId}
          onAddPrescription={addPrescription}
          onRemovePrescription={removePrescription}
          onUpdatePrescriptionName={updatePrescriptionName}
          onAddExercise={addExercise}
          onUpdateExercise={updateExercise}
          onRemoveExercise={removeExercise}
          prescriptionsEndRef={prescriptionsEndRef}
        />
      </div>

      {/* Modal de IA */}
      <WorkoutPlanAIModal
        isOpen={isAIModalOpen}
        onClose={handleCancelAI}
        description={aiDescription}
        onDescriptionChange={setAIDescription}
        onAnalyze={handleAIAnalyze}
        onApply={handleApplyAIResult}
        onBack={handleCancelAI}
        aiLoading={aiLoading}
        aiResult={aiResult}
        originalPrescriptions={prescriptions}
        professionalId={Number(professionalId)}
        userId={studentId}
        progress={aiProgress}
      />

      <WorkoutPlanFooter
        prescriptions={prescriptions}
        initialData={initialData}
        loading={loading}
        onClose={onClose}
      />
    </form>
  );
};

export default StudentWorkoutPlanForm;
